import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Calendar, Users, Newspaper, Image as ImageIcon,
  Video, Megaphone, Plus, Pencil, Trash2, Filter, X, Save, ArrowRight, Eye,
  Copy as CopyIcon, ScanLine, ShieldAlert, ShieldCheck, Activity, Mail, MessageCircle
} from "lucide-react";
import TwoFactorSettings from "../components/TwoFactorSettings";
import CampaignsAdmin from "../components/CampaignsAdmin";
import WhatsAppAdmin from "../components/WhatsAppAdmin";
import NewsAdmin from "../components/admin/NewsAdmin";
import NewsCategoriesAdmin from "../components/admin/NewsCategoriesAdmin";
import WebTVAdmin from "../components/admin/WebTVAdmin";
import WebTVCategoriesAdmin from "../components/admin/WebTVCategoriesAdmin";
import AdhesionsAdmin from "../components/admin/AdhesionsAdmin";
import EventImagesUpload from "../components/admin/EventImagesUpload";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const SECTIONS = [
  { k: "stats", l: "Tableau de bord", I: LayoutDashboard },
  { k: "adhesions", l: "Adhésions", I: Users },
  { k: "events", l: "Événements", I: Calendar },
  { k: "registrations", l: "Inscriptions", I: Users },
  { k: "duplicates", l: "Doublons", I: CopyIcon },
  { k: "campaigns", l: "Campagnes Email", I: Mail },
  { k: "whatsapp", l: "Campagne WhatsApp", I: MessageCircle },
  { k: "news", l: "Actualités", I: Newspaper },
  { k: "news-categories", l: "Catégories news", I: Newspaper },
  { k: "webtv", l: "Web TV", I: Newspaper },
  { k: "webtv-categories", l: "Catégories Web TV", I: Newspaper },
  { k: "media", l: "Médias", I: ImageIcon },
  { k: "conferences", l: "Conférences", I: Video },
  { k: "popup", l: "Popup promo", I: Megaphone },
  { k: "members", l: "Membres", I: Users },
  { k: "security", l: "Sécurité & logs", I: ShieldCheck },
];

const EVENT_DEFAULTS = { title: "", theme: "", description: "", date: "", time: "", city: "", country: "France", speakers: [], intervenants: [], stages: [], hide_location: false, is_featured: false, banner_image: "", images: [], videos: [], documents: [], published: true, archived: false };
const MEDIA_DEFAULTS = { title: "", type: "photo", url: "", thumbnail: "", description: "", event_id: "" };
const CONF_DEFAULTS = { title: "", description: "", scheduled_at: "", room_name: "", visibility: "public", allowed_member_ids: [] };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("stats");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Définition des permissions par rôle (sections visibles dans la sidebar)
  const ROLE_SECTIONS = {
    admin:         ["stats", "adhesions", "events", "registrations", "duplicates", "campaigns", "whatsapp", "news", "news-categories", "webtv", "webtv-categories", "media", "conferences", "popup", "members", "security"],
    responsable:   ["stats", "adhesions", "events", "registrations", "duplicates", "campaigns", "whatsapp", "news", "news-categories", "webtv", "webtv-categories", "media", "conferences", "popup", "members", "security"],
    protocole:     ["stats", "registrations"],
    securite:      ["stats", "registrations"],
    communication: ["stats", "news", "news-categories", "webtv", "webtv-categories", "media", "popup"],
    media:         ["stats", "news", "news-categories", "webtv", "webtv-categories", "media"],
    moderator:     ["stats", "registrations", "duplicates"],
  };
  const allowedSections = (user && ROLE_SECTIONS[user.role]) || [];
  const visibleSections = SECTIONS.filter((s) => allowedSections.includes(s.k));

  // Réoriente vers une section autorisée si l'actuelle ne l'est plus
  useEffect(() => {
    if (user && allowedSections.length > 0 && !allowedSections.includes(section)) {
      setSection(allowedSections[0]);
    }
  }, [user, allowedSections, section]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-bone lg:flex" data-testid="admin-dashboard">
      {/* Mobile topbar */}
      <div className="lg:hidden bg-ink text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div>
          <div className="font-serif text-base leading-tight">Front Uni</div>
          <div className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">Administration</div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Menu"
                className="w-9 h-9 grid place-items-center border border-white/20 rounded-sm">
          {sidebarOpen ? <X className="w-5 h-5" /> : <span className="text-lg">☰</span>}
        </button>
      </div>

      <aside className={`${sidebarOpen ? "block" : "hidden"} lg:block w-full lg:w-64 bg-ink text-white lg:sticky lg:top-0 lg:h-screen overflow-y-auto z-30`}>
        <div className="p-6 hidden lg:block">
          <div className="font-serif text-lg">Front Uni</div>
          <div className="eyebrow eyebrow-on-dark text-[0.6rem] mt-1">Administration · Diaspora</div>
          {user.role !== "admin" && (
            <div className="mt-2 inline-block px-2 py-0.5 bg-gold/20 text-gold text-[0.6rem] uppercase tracking-wider rounded">{user.role}</div>
          )}
        </div>
        <nav className="px-3 pb-6 space-y-1">
          {visibleSections.map((s) => (
            <button key={s.k} onClick={() => { setSection(s.k); setSidebarOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 rounded-sm text-sm flex items-center gap-3 transition-colors ${
                      section === s.k ? "bg-gold text-ink font-semibold" : "text-white/75 hover:bg-white/5"
                    }`}
                    data-testid={`nav-${s.k}`}>
              <s.I className="w-4 h-4" /> {s.l}
            </button>
          ))}
          <div className="mt-6 space-y-1.5">
            <Link to="/admin/scan" className="block px-4 py-2.5 rounded-sm text-sm text-gold hover:bg-gold/10 flex items-center gap-3 border border-gold/30"
                  data-testid="nav-scan">
              <ScanLine className="w-4 h-4" /> Scanner d'accès
            </Link>
            <Link to="/admin/inscription-rapide" className="block px-4 py-2.5 rounded-sm text-sm text-gold hover:bg-gold/10 flex items-center gap-3 border border-gold/30"
                  data-testid="nav-quick-register">
              <Plus className="w-4 h-4" /> Inscription rapide
            </Link>
            {["admin", "responsable"].includes(user.role) && (
              <Link to="/admin/accueil" className="block px-4 py-2.5 rounded-sm text-sm text-gold hover:bg-gold/10 flex items-center gap-3 border border-gold/30"
                    data-testid="nav-welcome">
                <Activity className="w-4 h-4" /> Écran d'accueil
              </Link>
            )}
          </div>
          <Link to="/" className="block px-4 py-2.5 rounded-sm text-sm text-white/60 hover:text-gold mt-2">
            ← Voir le site
          </Link>
          <button onClick={() => { logout(); nav("/admin/connexion"); }}
                  className="w-full text-left px-4 py-2.5 rounded-sm text-sm flex items-center gap-3 text-crimson hover:bg-white/5"
                  data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </nav>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
        <div className="flex items-start sm:items-center justify-between mb-6 sm:mb-8 gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="eyebrow text-gold-dark text-[0.6rem]">{SECTIONS.find((x) => x.k === section)?.l}</div>
            <h1 className="font-serif text-2xl sm:text-3xl text-ink mt-1 leading-tight truncate">{SECTIONS.find((x) => x.k === section)?.l}</h1>
          </div>
          <div className="text-xs sm:text-sm text-foreground/60 truncate max-w-[60vw]">{user?.email}</div>
        </div>

        {section === "stats" && <StatsView />}
        {section === "events" && <EventsAdmin />}
        {section === "registrations" && <RegistrationsAdmin />}
        {section === "duplicates" && <DuplicatesAdmin />}
        {section === "campaigns" && <CampaignsAdmin />}
        {section === "whatsapp" && <WhatsAppAdmin />}
        {section === "news" && <NewsAdmin />}
        {section === "news-categories" && <NewsCategoriesAdmin />}
        {section === "webtv" && <WebTVAdmin />}
        {section === "webtv-categories" && <WebTVCategoriesAdmin />}
        {section === "adhesions" && <AdhesionsAdmin />}
        {section === "media" && <MediaAdmin />}
        {section === "conferences" && <ConferencesAdmin />}
        {section === "popup" && <PopupAdmin />}
        {section === "members" && <MembersAdmin />}
        {section === "security" && <SecurityAdmin />}
      </main>
    </div>
  );
}

// ============ STATS ============
function StatsView() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    ["Événements publiés", s.events_published, Calendar],
    ["Inscriptions", s.registrations, Users],
    ["Actualités", s.news, Newspaper],
    ["Médias", s.media, ImageIcon],
    ["Membres", s.members, Users],
    ["Conférences", s.conferences, Video],
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map(([l, v, I]) => (
        <div key={l} className="bg-white border border-border rounded-sm p-6">
          <I className="w-5 h-5 text-gold-dark" />
          <div className="text-[0.7rem] uppercase tracking-[0.18em] font-semibold text-foreground/60 mt-4">{l}</div>
          <div className="font-serif text-4xl text-ink mt-2">{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

// ============ EVENTS ============
function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      const payload = { ...form, speakers: form.speakers.filter(Boolean), images: form.images.filter(Boolean), videos: form.videos.filter(Boolean), documents: form.documents.filter(Boolean) };
      if (editing?.id) await api.put(`/admin/events/${editing.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Événement enregistré");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer cet événement ?")) return;
    await api.delete(`/admin/events/${id}`); toast.success("Supprimé"); load();
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setEditing({ ...EVENT_DEFAULTS })} className="btn-primary" data-testid="event-create">
          <Plus className="w-4 h-4" /> Nouvel événement
        </button>
      </div>
      <DataTable
        rows={items}
        cols={[
          { k: "title", l: "Titre" },
          { k: "date", l: "Date" },
          { k: "city", l: "Ville" },
          { k: "country", l: "Pays" },
          { k: "published", l: "Publié", r: (v) => v ? "Oui" : "Non" },
        ]}
        actions={(r) => (
          <>
            <a target="_blank" rel="noreferrer" href={`/i/${r.slug}`} className="text-foreground/60 hover:text-ink" title="Lien d'inscription"><Eye className="w-4 h-4" /></a>
            <button onClick={() => setEditing(r)} className="text-ink hover:text-ink-deep" data-testid={`edit-event-${r.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(r.id)} className="text-crimson"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      />
      {editing && <EventForm value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EventForm({ value, onClose, onSave }) {
  const [f, setF] = useState({
    ...value,
    speakers: value.speakers || [], images: value.images || [],
    videos: value.videos || [], documents: value.documents || [],
    stages: value.stages || [], intervenants: value.intervenants || [],
  });
  const addStage = () => setF({ ...f, stages: [...f.stages, { label: "", city: "", country: "", date: "", time: "", location_note: "Lieu communiqué après validation de l'inscription" }] });
  const updateStage = (i, k, v) => { const s = [...f.stages]; s[i] = { ...s[i], [k]: v }; setF({ ...f, stages: s }); };
  const removeStage = (i) => setF({ ...f, stages: f.stages.filter((_, j) => j !== i) });
  const addInt = () => setF({ ...f, intervenants: [...f.intervenants, { name: "", title: "" }] });
  const updateInt = (i, k, v) => { const s = [...f.intervenants]; s[i] = { ...s[i], [k]: v }; setF({ ...f, intervenants: s }); };
  const removeInt = (i) => setF({ ...f, intervenants: f.intervenants.filter((_, j) => j !== i) });
  return (
    <Modal title={value.id ? "Modifier l'événement" : "Nouvel événement"} onClose={onClose}>
      <div className="space-y-4">
        <Inp l="Titre" v={f.title} on={(v) => setF({ ...f, title: v })} test="event-title" />
        <Inp l="Thème" v={f.theme} on={(v) => setF({ ...f, theme: v })} test="event-theme" />
        <Area l="Description" v={f.description} on={(v) => setF({ ...f, description: v })} test="event-description" />
        <div className="grid grid-cols-2 gap-3">
          <Inp l="Date principale (YYYY-MM-DD)" v={f.date} on={(v) => setF({ ...f, date: v })} test="event-date" />
          <Inp l="Heure" v={f.time || ""} on={(v) => setF({ ...f, time: v })} test="event-time" />
          <Inp l="Ville principale" v={f.city} on={(v) => setF({ ...f, city: v })} test="event-city" />
          <Inp l="Pays principal" v={f.country} on={(v) => setF({ ...f, country: v })} test="event-country" />
        </div>
        <EventImagesUpload
          banner={f.banner_image || ""}
          onBanner={(url) => setF({ ...f, banner_image: url })}
          images={f.images}
          onImages={(imgs) => setF({ ...f, images: imgs })}
        />

        {/* STAGES */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow text-foreground/60">Étapes multi-lieux (optionnel)</div>
            <button onClick={addStage} className="text-xs text-ink font-semibold hover:underline" data-testid="add-stage">+ Ajouter une étape</button>
          </div>
          {f.stages.map((s, i) => (
            <div key={i} className="bg-bone rounded-sm border border-border p-3 mb-2 space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs text-foreground/55">Étape {i + 1}</span>
                <button onClick={() => removeStage(i)} className="text-crimson text-xs">Retirer</button></div>
              <div className="grid grid-cols-2 gap-2">
                <Inp l="Label" v={s.label || ""} on={(v) => updateStage(i, "label", v)} test={`stage-${i}-label`} />
                <Inp l="Ville" v={s.city} on={(v) => updateStage(i, "city", v)} test={`stage-${i}-city`} />
                <Inp l="Pays" v={s.country} on={(v) => updateStage(i, "country", v)} test={`stage-${i}-country`} />
                <Inp l="Date (YYYY-MM-DD)" v={s.date} on={(v) => updateStage(i, "date", v)} test={`stage-${i}-date`} />
                <Inp l="Heure" v={s.time || ""} on={(v) => updateStage(i, "time", v)} test={`stage-${i}-time`} />
                <Inp l="Note de lieu" v={s.location_note || ""} on={(v) => updateStage(i, "location_note", v)} test={`stage-${i}-note`} />
              </div>
            </div>
          ))}
        </div>

        {/* INTERVENANTS */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow text-foreground/60">Intervenants (optionnel)</div>
            <button onClick={addInt} className="text-xs text-ink font-semibold hover:underline" data-testid="add-int">+ Ajouter un intervenant</button>
          </div>
          {f.intervenants.map((p, i) => (
            <div key={i} className="bg-bone rounded-sm border border-border p-3 mb-2">
              <div className="flex justify-between items-center mb-2"><span className="text-xs text-foreground/55">#{i + 1}</span>
                <button onClick={() => removeInt(i)} className="text-crimson text-xs">Retirer</button></div>
              <div className="grid grid-cols-2 gap-2">
                <Inp l="Nom" v={p.name} on={(v) => updateInt(i, "name", v)} test={`int-${i}-name`} />
                <Inp l="Titre / Fonction" v={p.title || ""} on={(v) => updateInt(i, "title", v)} test={`int-${i}-title`} />
              </div>
            </div>
          ))}
        </div>

        <Inp l="Speakers texte (séparés par virgule)" v={f.speakers.join(", ")} on={(v) => setF({ ...f, speakers: v.split(",").map((x) => x.trim()).filter(Boolean) })} test="event-speakers" />

        <div className="flex items-center gap-6 flex-wrap pt-3 border-t border-border">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} /> Publié</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.archived} onChange={(e) => setF({ ...f, archived: e.target.checked })} /> Archivé</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.hide_location} onChange={(e) => setF({ ...f, hide_location: e.target.checked })} data-testid="event-hide-location" /> Masquer le lieu exact</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} data-testid="event-featured" /> <strong>À la une (popup auto)</strong></label>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button onClick={onClose} className="btn-outline">Annuler</button>
          <button onClick={() => onSave(f)} className="btn-primary" data-testid="event-save"><Save className="w-4 h-4" /> Enregistrer</button>
        </div>
      </div>
    </Modal>
  );
}

// ============ REGISTRATIONS ============
function RegistrationsAdmin() {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({}); // event_id -> count
  const [filters, setF] = useState({ event_id: "", country: "", city: "", political_party: "", association: "", status: "", selected_stage: "", search: "", view: "full", presence: "" });
  const load = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && !["search", "view"].includes(k)) p.set(k, v);
    });
    api.get(`/admin/registrations?${p.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [filters.event_id, filters.country, filters.city, filters.political_party, filters.association, filters.status, filters.selected_stage]);
  useEffect(() => { api.get("/admin/events").then((r) => setEvents(r.data)); }, []);

  // Recherche globale intelligente : nom, prénom, email, téléphone, N°, ville, pays, parti, association, fonction, étape
  const q = (filters.search || "").trim().toLowerCase();
  // Exclure les inscriptions révoquées de l'affichage et des exports (listes propres)
  const baseRows = rows.filter((r) => r.status !== "revoked");
  let matched = !q ? baseRows : baseRows.filter((r) =>
    [r.first_name, r.last_name, r.email, r.registration_number, r.phone,
     r.city, r.country, r.political_party_name, r.association_name,
     r.function, r.selected_stage]
      .some((v) => (v || "").toString().toLowerCase().includes(q))
  );
  // Filtre rapide présence
  if (filters.presence === "present") matched = matched.filter((r) => r.checked_in_at);
  else if (filters.presence === "absent") matched = matched.filter((r) => !r.checked_in_at);
  // Tri alphabétique : Nom puis Prénom (insensible à la casse)
  const filteredRows = [...matched].sort((a, b) => {
    const ln = (a.last_name || "").localeCompare(b.last_name || "", "fr", { sensitivity: "base" });
    if (ln !== 0) return ln;
    return (a.first_name || "").localeCompare(b.first_name || "", "fr", { sensitivity: "base" });
  });

  // Stats par pays (sur tout le dataset, hors révoqués, scope = événement filtré ou tous)
  const countryStats = baseRows.reduce((acc, r) => {
    const k = (r.country || "—").trim() || "—";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const sortedCountries = Object.entries(countryStats).sort((a, b) => b[1] - a[1]);

  // Compute counts per event (one extra call without event filter)
  useEffect(() => {
    api.get("/admin/registrations").then((r) => {
      const c = {};
      r.data.forEach((row) => {
        if (row.status === "revoked") return;
        c[row.event_id] = (c[row.event_id] || 0) + 1;
      });
      setCounts({ ...c, __total__: r.data.filter((x) => x.status !== "revoked").length });
    });
  }, [rows.length]);

  const exportCSV = () => {
    const headers = ["Numéro", "Nom", "Prénom", "Email", "Téléphone", "Ville", "Pays", "Étape choisie", "Parti", "Association", "Fonction", "Événement", "Date", "Présence", "Heure de scan"];
    // Tri alpha + sans révoqués
    const lines = filteredRows.map((r) => [r.registration_number, r.last_name, r.first_name, r.email, r.phone, r.city, r.country, r.selected_stage || "", r.political_party_name || "", r.association_name || "", r.function || "", r.event_title, r.created_at, r.checked_in_at ? "Présent" : "Absent", r.checked_in_at || ""].map((x) => `"${(x || "").toString().replace(/"/g, "''")}"`).join(","));
    const blob = new Blob(["\uFEFF" + [headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "inscriptions.csv"; a.click();
  };

  // Helper colonne présence (badge couleur + case vide pour signature manuelle à l'impression)
  const presenceCol = {
    k: "checked_in_at",
    l: "Présence",
    r: (v) => v
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[0.7rem] font-bold">✓ Présent</span>
      : <span className="inline-block w-16 h-5 border border-foreground/30 rounded-sm print:border-black"></span>,
  };

  const selectedEvent = events.find((e) => e.id === filters.event_id);

  return (
    <div>
      {/* EVENT TABS — separation par événement */}
      <div className="mb-5" data-testid="event-tabs">
        <div className="eyebrow text-foreground/60 mb-3">Inscriptions par événement</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setF({ ...filters, event_id: "" })}
                  data-testid="event-tab-all"
                  className={`px-4 py-2.5 rounded-sm text-sm border transition-colors ${
                    !filters.event_id ? "bg-ink text-white border-ink" : "bg-white border-border hover:border-ink/50"
                  }`}>
            Toutes <span className={`ml-2 text-xs ${!filters.event_id ? "text-gold" : "text-foreground/55"}`}>
              {counts.__total__ || 0}
            </span>
          </button>
          {events.map((e) => {
            const active = filters.event_id === e.id;
            return (
              <button key={e.id}
                      onClick={() => setF({ ...filters, event_id: e.id, selected_stage: "" })}
                      data-testid={`event-tab-${e.slug}`}
                      title={e.title}
                      className={`px-4 py-2.5 rounded-sm text-sm border transition-colors max-w-xs truncate ${
                        active ? "bg-ink text-white border-ink" : "bg-white border-border hover:border-ink/50"
                      }`}>
                {e.title.length > 32 ? e.title.slice(0, 32) + "…" : e.title}
                <span className={`ml-2 text-xs ${active ? "text-gold" : "text-foreground/55"}`}>
                  {counts[e.id] || 0}
                </span>
              </button>
            );
          })}
        </div>
        {selectedEvent && (
          <div className="mt-3 text-xs text-foreground/60">
            Événement actif : <span className="font-semibold text-ink">{selectedEvent.title}</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-sm p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="w-4 h-4 text-gold-dark" />
          <span className="eyebrow text-foreground/60">Recherche & filtres</span>
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={filters.view === "simple"} onChange={(e) => setF({ ...filters, view: e.target.checked ? "simple" : "full" })}
                     data-testid="toggle-simple-view" />
              Vue simplifiée
            </label>
            <button onClick={() => window.print()} className="btn-outline text-xs" data-testid="print-list">
              🖨 Imprimer
            </button>
            <button onClick={exportCSV} className="btn-outline text-xs" data-testid="export-csv">Exporter CSV</button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 print:hidden">
          <div className="lg:col-span-2">
            <Inp l="🔍 Recherche intelligente (nom, ville, pays, parti, asso, tél…)" v={filters.search || ""} on={(v) => setF({ ...filters, search: v })} test="filter-search" />
          </div>
          <Inp l="Pays" v={filters.country} on={(v) => setF({ ...filters, country: v })} test="filter-country" />
          <Inp l="Ville" v={filters.city} on={(v) => setF({ ...filters, city: v })} test="filter-city" />
          <Inp l="Étape" v={filters.selected_stage} on={(v) => setF({ ...filters, selected_stage: v })} test="filter-stage" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2 print:hidden">
          <Inp l="Parti politique" v={filters.political_party} on={(v) => setF({ ...filters, political_party: v })} test="filter-party" />
          <Inp l="Association" v={filters.association} on={(v) => setF({ ...filters, association: v })} test="filter-asso" />
          <Inp l="Statut (confirmed / revoked)" v={filters.status} on={(v) => setF({ ...filters, status: v })} test="filter-status" />
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="font-bold text-ink">{filteredRows.length}</span>
            <span className="text-foreground/55"> résultat(s) {q || filters.country || filters.city ? "filtré(s)" : ""} sur {baseRows.length} inscriptions (révoquées exclues)</span>
            <span className="ml-3 text-emerald-700 font-semibold">✓ {filteredRows.filter((r) => r.checked_in_at).length} présent(s)</span>
            <span className="ml-2 text-foreground/55">/ {filteredRows.filter((r) => !r.checked_in_at).length} absent(s)</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setF({ ...filters, presence: filters.presence === "present" ? "" : "present" })}
                    className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                      filters.presence === "present" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-border hover:border-emerald-500"
                    }`}
                    data-testid="filter-presence-present">
              ✓ Présents
            </button>
            <button onClick={() => setF({ ...filters, presence: filters.presence === "absent" ? "" : "absent" })}
                    className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                      filters.presence === "absent" ? "bg-foreground text-white border-foreground" : "bg-white border-border hover:border-foreground"
                    }`}
                    data-testid="filter-presence-absent">
              Absents
            </button>
          </div>
        </div>
      </div>

      {/* Stats par pays */}
      {sortedCountries.length > 0 && (
        <div className="bg-white border border-border rounded-sm p-4 mb-4 print:hidden" data-testid="country-stats">
          <div className="eyebrow text-foreground/60 mb-3">📊 Répartition par pays</div>
          <div className="flex flex-wrap gap-2">
            {sortedCountries.slice(0, 12).map(([country, n]) => (
              <button key={country} onClick={() => setF({ ...filters, country: country === filters.country ? "" : country })}
                      className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${
                        filters.country === country ? "bg-ink text-white border-ink" : "bg-bone border-border hover:border-ink/50"
                      }`}
                      data-testid={`country-chip-${country}`}>
                {country} <span className={`ml-1.5 font-bold ${filters.country === country ? "text-gold" : "text-foreground/60"}`}>{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="print:block">
        <div className="hidden print:block mb-4">
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 22, color: "#0A2540" }}>Liste des inscrits</h1>
          <div style={{ fontSize: 12, color: "#666" }}>{selectedEvent?.title || "Tous événements"} · {filteredRows.length} participants</div>
        </div>
        <DataTable
          rows={filteredRows}
          cols={filters.view === "simple" ? [
            { k: "last_name", l: "Nom" },
            { k: "first_name", l: "Prénom" },
            { k: "phone", l: "Téléphone" },
            { k: "email", l: "Email" },
            presenceCol,
          ] : [
            { k: "registration_number", l: "N°" },
            { k: "first_name", l: "Prénom" },
            { k: "last_name", l: "Nom" },
            { k: "phone", l: "Téléphone" },
            { k: "email", l: "Email" },
            { k: "selected_stage", l: "Étape" },
            { k: "city", l: "Ville" },
            { k: "country", l: "Pays" },
            presenceCol,
            { k: "event_title", l: "Événement" },
          ]}
        />
      </div>
    </div>
  );
}

// ============ MEDIA ============
function MediaAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (f) => { try { await api.post("/admin/media", f); toast.success("Média ajouté"); setEditing(null); load(); } catch { toast.error("Erreur"); } };
  const del = async (id) => { if (!confirm("Supprimer ?")) return; await api.delete(`/admin/media/${id}`); load(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setEditing({ ...MEDIA_DEFAULTS })} className="btn-primary" data-testid="media-create"><Plus className="w-4 h-4" /> Nouveau média</button>
      </div>
      <DataTable rows={items} cols={[
        { k: "title", l: "Titre" }, { k: "type", l: "Type" }, { k: "url", l: "URL" },
      ]} actions={(r) => (<button onClick={() => del(r.id)} className="text-crimson"><Trash2 className="w-4 h-4" /></button>)} />
      {editing && (
        <Modal title="Nouveau média" onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Inp l="Titre" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="media-title" />
            <Select l="Type" v={editing.type} on={(v) => setEditing({ ...editing, type: v })}
                    opts={[["photo", "Photo"], ["video", "Vidéo"], ["replay", "Replay"], ["interview", "Interview"], ["conference", "Conférence"], ["archive", "Archive"]]} test="media-type" />
            <Inp l="URL (image ou iframe vidéo)" v={editing.url} on={(v) => setEditing({ ...editing, url: v })} test="media-url" />
            <Inp l="Miniature (optionnel)" v={editing.thumbnail || ""} on={(v) => setEditing({ ...editing, thumbnail: v })} test="media-thumb" />
            <Area l="Description" v={editing.description || ""} on={(v) => setEditing({ ...editing, description: v })} rows={2} test="media-desc" />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn-outline">Annuler</button><button onClick={() => save(editing)} className="btn-primary" data-testid="media-save">Enregistrer</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ CONFERENCES ============
function ConferencesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/conferences").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (f) => { try { await api.post("/admin/conferences", f); toast.success("Conférence créée"); setEditing(null); load(); } catch { toast.error("Erreur"); } };
  const del = async (id) => { if (!confirm("Supprimer ?")) return; await api.delete(`/admin/conferences/${id}`); load(); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setEditing({ ...CONF_DEFAULTS, room_name: `fur-${Math.random().toString(36).slice(2, 8)}` })} className="btn-primary" data-testid="conf-create"><Plus className="w-4 h-4" /> Nouvelle conférence</button>
      </div>
      <DataTable rows={items} cols={[
        { k: "title", l: "Titre" }, { k: "scheduled_at", l: "Date" }, { k: "visibility", l: "Visibilité" }, { k: "room_name", l: "Salle" },
      ]} actions={(r) => (<button onClick={() => del(r.id)} className="text-crimson"><Trash2 className="w-4 h-4" /></button>)} />
      {editing && (
        <Modal title="Nouvelle conférence" onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Inp l="Titre" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="conf-title" />
            <Area l="Description" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} test="conf-desc" />
            <Inp l="Date et heure (ISO)" v={editing.scheduled_at} on={(v) => setEditing({ ...editing, scheduled_at: v })} test="conf-date" />
            <Inp l="Nom de la salle (room name)" v={editing.room_name} on={(v) => setEditing({ ...editing, room_name: v })} test="conf-room" />
            <Select l="Visibilité" v={editing.visibility} on={(v) => setEditing({ ...editing, visibility: v })}
                    opts={[["public", "Publique"], ["private", "Privée"]]} test="conf-visibility" />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn-outline">Annuler</button><button onClick={() => save(editing)} className="btn-primary" data-testid="conf-save">Enregistrer</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ POPUP ============
function PopupAdmin() {
  const [cfg, setCfg] = useState({ enabled: false, event_slug: "", title: "", subtitle: "" });
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/popup").then((r) => setCfg({ ...cfg, ...r.data, event_slug: r.data?.event_slug || "", title: r.data?.title || "", subtitle: r.data?.subtitle || "" }));
    api.get("/admin/events").then((r) => setEvents(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async () => {
    try { await api.put("/admin/popup", { ...cfg, event_slug: cfg.event_slug || null, title: cfg.title || null, subtitle: cfg.subtitle || null }); toast.success("Popup mis à jour"); } catch { toast.error("Erreur"); }
  };
  return (
    <div className="bg-white border border-border rounded-sm p-8 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-ink">Popup promotionnel</h2>
          <p className="text-sm text-foreground/65 mt-1">S'affiche sur la vitrine quand un événement est actif.</p>
        </div>
        <label className="flex items-center gap-3">
          <span className="text-sm font-semibold">Activé</span>
          <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${cfg.enabled ? "bg-ink" : "bg-foreground/20"}`} data-testid="popup-toggle">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-6" : ""}`} />
          </button>
        </label>
      </div>
      <div className="mt-6 space-y-4">
        <Select l="Événement lié" v={cfg.event_slug} on={(v) => setCfg({ ...cfg, event_slug: v })}
                opts={[["", "—"], ...events.map((e) => [e.slug, e.title])]} test="popup-event" />
        <Inp l="Titre" v={cfg.title} on={(v) => setCfg({ ...cfg, title: v })} test="popup-title" />
        <Area l="Sous-titre" v={cfg.subtitle} on={(v) => setCfg({ ...cfg, subtitle: v })} rows={2} test="popup-subtitle" />
        <button onClick={save} className="btn-primary" data-testid="popup-save">Enregistrer <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// ============ MEMBERS (full CRUD + rôles) ============
const ROLES = ["admin", "responsable", "protocole", "securite", "communication", "media", "moderator", "member"];

function MembersAdmin() {
  const [m, setM] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [editing, setEditing] = useState(null); // member object or "new"
  const [createdInfo, setCreatedInfo] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (roleFilter) q.set("role", roleFilter);
    api.get(`/admin/members?${q.toString()}`).then((r) => setM(r.data));
  };
  useEffect(() => { load(); }, [search, roleFilter]);

  const remove = async (mem) => {
    if (!confirm(`Supprimer définitivement ${mem.email} ?`)) return;
    await api.delete(`/admin/members/${mem.id}`);
    toast.success("Membre supprimé");
    load();
  };

  return (
    <div data-testid="members-admin">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="eyebrow text-gold-dark">Communauté</div>
          <h2 className="font-serif text-2xl text-ink mt-1">Membres & rôles</h2>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary text-sm" data-testid="new-member-btn">
          <Plus className="w-4 h-4" /> Nouveau membre
        </button>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <Inp l="🔍 Recherche (nom/email)" v={search} on={setSearch} test="members-search" />
        <Select l="Rôle" v={roleFilter} on={setRoleFilter} opts={[["", "Tous"], ...ROLES.map((r) => [r, r])]} test="members-role-filter" />
        <div className="text-sm text-foreground/60 ml-auto">{m.length} membre(s)</div>
      </div>

      <DataTable rows={m}
        cols={[
          { k: "first_name", l: "Prénom" }, { k: "last_name", l: "Nom" },
          { k: "email", l: "Email" },
          { k: "role", l: "Rôle", r: (v) => <span className="px-2 py-0.5 bg-ink/5 rounded text-[0.7rem] font-mono">{v}</span> },
          { k: "phone", l: "Tél." },
          { k: "city", l: "Ville" },
        ]}
        actions={(r) => (
          <>
            <button onClick={() => setEditing(r)} className="text-foreground/55 hover:text-ink" title="Modifier" data-testid={`edit-${r.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(r)} className="text-foreground/55 hover:text-crimson" title="Supprimer" data-testid={`del-${r.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}
      />

      {editing && <MemberFormModal member={editing === "new" ? null : editing}
                                   onClose={() => setEditing(null)}
                                   onCreated={(info) => { setCreatedInfo(info); load(); }}
                                   onSaved={() => { setEditing(null); load(); }} />}
      {createdInfo && <CredentialsModal info={createdInfo} onClose={() => setCreatedInfo(null)} />}
    </div>
  );
}

function MemberFormModal({ member, onClose, onCreated, onSaved }) {
  const isNew = !member;
  const [f, setF] = useState(member || { first_name: "", last_name: "", email: "", role: "member", phone: "", send_welcome: true });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) {
        const r = await api.post("/admin/members", f);
        toast.success("Membre créé");
        onCreated(r.data);
        onClose();
      } else {
        await api.put(`/admin/members/${member.id}`, {
          first_name: f.first_name, last_name: f.last_name,
          role: f.role, phone: f.phone, city: f.city, country: f.country, function: f.function,
        });
        toast.success("Membre mis à jour");
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
      <form onSubmit={submit} className="relative bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow text-gold-dark">{isNew ? "Nouveau membre" : "Modifier"}</div>
        <h2 className="font-serif text-2xl text-ink mt-1 mb-5">{isNew ? "Créer un membre" : `${member.first_name} ${member.last_name}`}</h2>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Inp l="Prénom" v={f.first_name} on={(v) => setF({ ...f, first_name: v })} test="member-fn" />
            <Inp l="Nom" v={f.last_name} on={(v) => setF({ ...f, last_name: v })} test="member-ln" />
          </div>
          <Inp l="Email" v={f.email} on={(v) => setF({ ...f, email: v })} test="member-email" disabled={!isNew} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Select l="Rôle" v={f.role} on={(v) => setF({ ...f, role: v })} opts={ROLES.map((r) => [r, r])} test="member-role" />
            <Inp l="Téléphone" v={f.phone || ""} on={(v) => setF({ ...f, phone: v })} test="member-phone" />
          </div>
          {!isNew && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Inp l="Ville" v={f.city || ""} on={(v) => setF({ ...f, city: v })} test="member-city" />
              <Inp l="Pays" v={f.country || ""} on={(v) => setF({ ...f, country: v })} test="member-country" />
            </div>
          )}
          {!isNew && (
            <PasswordResetBlock memberId={member.id} email={member.email} />
          )}
          {isNew && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.send_welcome} onChange={(e) => setF({ ...f, send_welcome: e.target.checked })} data-testid="member-welcome" />
              Envoyer l'email de bienvenue avec mot de passe temporaire
            </label>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline text-sm">Annuler</button>
          <button disabled={busy} className="btn-primary text-sm" data-testid="member-save">
            {busy ? "..." : isNew ? "Créer" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordResetBlock({ memberId, email }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!pwd || pwd.length < 4) return toast.error("4 caractères minimum");
    if (!confirm(`Définir le mot de passe « ${pwd} » pour ${email} ?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/members/${memberId}/set-password`, { new_password: pwd });
      setDone(r.data.new_password_displayed_to_admin);
      toast.success("Mot de passe mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="border-t border-border pt-4 mt-2">
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
          <div className="eyebrow text-emerald-700">Mot de passe défini</div>
          <div className="mt-2 text-sm">Communiquez ces identifiants au membre :</div>
          <div className="mt-2 bg-white border p-3 rounded-sm font-mono text-sm">
            <div><strong>Email :</strong> {email}</div>
            <div><strong>Mot de passe :</strong> {done}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-4 mt-2">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="pwd-reset-open"
                className="text-sm text-foreground/70 hover:text-ink inline-flex items-center gap-1.5">
          🔑 Réinitialiser le mot de passe
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-foreground/55">Nouveau mot de passe (simple OK : ex. Proto2026)</div>
          <div className="flex gap-2">
            <input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus
                   placeholder="Proto2026" data-testid="pwd-reset-input"
                   className="flex-1 border border-border rounded-sm p-2.5 text-sm font-mono" />
            <button type="button" onClick={submit} disabled={busy} data-testid="pwd-reset-submit"
                    className="px-4 py-2 bg-ink text-white text-sm rounded-sm hover:bg-ink/90 disabled:opacity-50">
              {busy ? "…" : "Définir"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setPwd(""); }}
                    className="text-foreground/55 hover:text-foreground px-2 text-sm">Annuler</button>
          </div>
          <div className="text-[0.7rem] text-foreground/55">⚠️ Le membre devra utiliser ce nouveau mot de passe à sa prochaine connexion.</div>
        </div>
      )}
    </div>
  );
}

function CredentialsModal({ info, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center p-4 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-md p-7 rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow text-gold-dark">Identifiants temporaires</div>
        <h3 className="font-serif text-xl text-ink mt-1">Compte créé</h3>
        <p className="text-sm text-foreground/65 mt-3">Notez ces informations — le mot de passe ne sera plus affiché.</p>
        <div className="mt-4 bg-bone border border-border rounded-sm p-4 font-mono text-sm space-y-2">
          <div><span className="text-foreground/55 text-xs uppercase tracking-wider">Email</span><br />{info.email}</div>
          <div><span className="text-foreground/55 text-xs uppercase tracking-wider">Mot de passe</span><br />{info.temp_password_displayed_to_admin}</div>
        </div>
        <p className="text-xs text-foreground/55 mt-3">
          {info.welcome_email_sent ? "✓ Email de bienvenue envoyé au membre." : "Email de bienvenue non envoyé (option désactivée ou erreur)."}
        </p>
        <button onClick={onClose} className="btn-primary w-full justify-center mt-4" data-testid="creds-close">J'ai noté, fermer</button>
      </div>
    </div>
  );
}


// ============ Utils ============
function DataTable({ rows, cols, actions }) {
  if (!rows?.length) return <div className="bg-white border border-border rounded-sm p-12 text-center text-foreground/55">Aucune donnée.</div>;
  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-bone border-b border-border">
          <tr>
            {cols.map((c) => <th key={c.k} className="text-left p-3 eyebrow text-foreground/60">{c.l}</th>)}
            {actions && <th className="p-3"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.slug} className="border-b border-border last:border-0 hover:bg-bone/50">
              {cols.map((c) => <td key={c.k} className="p-3 text-foreground/85">{c.r ? c.r(r[c.k]) : (r[c.k] ?? "—")}</td>)}
              {actions && <td className="p-3 text-right"><div className="inline-flex gap-3">{actions(r)}</div></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white max-w-2xl w-full rounded-sm shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white">
          <h3 className="font-serif text-xl text-ink">{title}</h3>
          <button onClick={onClose} className="text-foreground/60 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Inp({ l, v, on, test }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{l}</label>
      <input value={v ?? ""} onChange={(e) => on(e.target.value)}
             className="w-full border border-border rounded-sm p-2.5 text-sm focus:border-ink outline-none" data-testid={test} />
    </div>
  );
}
function Area({ l, v, on, rows = 4, test }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{l}</label>
      <textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)}
                className="w-full border border-border rounded-sm p-2.5 text-sm focus:border-ink outline-none" data-testid={test} />
    </div>
  );
}
function Select({ l, v, on, opts, test }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{l}</label>
      <select value={v ?? ""} onChange={(e) => on(e.target.value)}
              className="w-full border border-border rounded-sm p-2.5 text-sm bg-white focus:border-ink outline-none" data-testid={test}>
        {opts.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
      </select>
    </div>
  );
}


// ============ DUPLICATES ============
function DuplicatesAdmin() {
  const [data, setData] = useState({ summary: { total_groups: 0, by_count: {} }, groups: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // key -> bool
  const [busy, setBusy] = useState({}); // reg_id -> bool

  const load = () => {
    setLoading(true);
    api.get("/admin/registrations/duplicates").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const revoke = async (reg) => {
    if (!confirm(`Confirmer la révocation de l'inscription ${reg.registration_number} (${reg.first_name} ${reg.last_name}) ?\n\nUn email d'annulation sera envoyé à ${reg.email} et le QR code sera invalidé.`)) return;
    setBusy({ ...busy, [reg.id]: true });
    try {
      await api.post(`/admin/registrations/${reg.id}/revoke`);
      toast.success(`Inscription ${reg.registration_number} révoquée`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy({ ...busy, [reg.id]: false });
    }
  };

  if (loading) return <div className="text-foreground/60 text-sm">Chargement…</div>;

  return (
    <div data-testid="duplicates-admin">
      {/* Summary cards */}
      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        <SumCard label="Groupes détectés" value={data.summary.total_groups} icon={ShieldAlert} />
        {["2", "3", "4", "5", "6"].filter((c) => data.summary.by_count[c]).map((c) => (
          <SumCard key={c} label={`Doublons ×${c}`} value={data.summary.by_count[c]} icon={CopyIcon} />
        ))}
      </div>

      {data.groups.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-8 text-center text-foreground/55">
          Aucun doublon détecté. Tous les inscrits sont uniques. ✓
        </div>
      ) : (
        <div className="space-y-3">
          {data.groups.map((g) => (
            <div key={g.key} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`dup-group-${g.key}`}>
              <button onClick={() => setExpanded({ ...expanded, [g.key]: !expanded[g.key] })}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-bone transition-colors">
                <div className="flex items-center gap-4 text-left">
                  <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-sm text-xs font-bold ${
                    g.count >= 4 ? "bg-crimson text-white" : g.count >= 3 ? "bg-orange-500 text-white" : "bg-gold/20 text-ink"
                  }`}>×{g.count}</span>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-[0.16em] text-foreground/55">
                      {g.type === "email" ? "Email partagé" : "Téléphone partagé"} · {g.event_title}
                    </div>
                    <div className="font-serif text-ink text-base mt-0.5">{g.value}</div>
                  </div>
                </div>
                <ArrowRight className={`w-4 h-4 text-foreground/40 transition-transform ${expanded[g.key] ? "rotate-90" : ""}`} />
              </button>
              {expanded[g.key] && (
                <div className="border-t border-border bg-bone/50 px-5 py-4 space-y-2">
                  {g.registrations.map((r, i) => (
                    <div key={r.id} className="bg-white border border-border p-4 rounded-sm flex items-start justify-between gap-4 text-sm"
                         data-testid={`dup-reg-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs bg-ink/5 px-2 py-0.5 rounded">{r.registration_number}</span>
                          {i === 0 && <span className="text-[0.6rem] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">le + ancien</span>}
                          {r.checked_in_at && <span className="text-[0.6rem] uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded">déjà présent</span>}
                        </div>
                        <div className="mt-1.5 font-semibold text-ink">{r.first_name} {r.last_name}</div>
                        <div className="text-foreground/65 text-xs mt-0.5">
                          {r.email} · {r.phone || "—"} · {r.city || "—"}, {r.country || "—"} · {r.selected_stage || "Étape non précisée"}
                        </div>
                        <div className="text-foreground/45 text-[0.7rem] mt-0.5">
                          Inscrit le {new Date(r.created_at).toLocaleString("fr-FR")}
                        </div>
                      </div>
                      <button onClick={() => revoke(r)}
                              disabled={busy[r.id]}
                              data-testid={`revoke-${r.id}`}
                              className="px-3 py-1.5 text-xs border border-crimson text-crimson hover:bg-crimson hover:text-white rounded-sm transition-colors flex items-center gap-1.5 disabled:opacity-50">
                        <Trash2 className="w-3 h-3" /> {busy[r.id] ? "..." : "Révoquer"}
                      </button>
                    </div>
                  ))}
                  <div className="text-[0.7rem] text-foreground/55 italic pt-1">
                    Conseil : conservez le plus ancien (en règle générale, c'est l'inscription initiale légitime) et révoquez les autres.
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SumCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-border rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div className="text-[0.65rem] uppercase tracking-[0.16em] text-foreground/60">{label}</div>
        <Icon className="w-4 h-4 text-gold-dark" />
      </div>
      <div className="font-serif text-3xl text-ink mt-2">{value}</div>
    </div>
  );
}


// ============ SECURITY (2FA + logs) ============
function SecurityAdmin() {
  const [show2FA, setShow2FA] = useState(false);
  const [loginLogs, setLoginLogs] = useState([]);
  const [actionLogs, setActionLogs] = useState([]);
  const [tab, setTab] = useState("logins");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [me, setMe] = useState(null);

  const refresh = () => {
    api.get(`/admin/login-logs?limit=100${onlyFailed ? "&only_failed=true" : ""}`).then((r) => setLoginLogs(r.data));
    api.get("/admin/action-logs?limit=100").then((r) => setActionLogs(r.data));
    api.get("/auth/me").then((r) => setMe(r.data));
  };
  useEffect(() => { refresh(); }, [onlyFailed]);

  return (
    <div data-testid="security-admin">
      <div className="bg-white border border-border rounded-sm p-5 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-ink text-gold rounded-sm grid place-items-center"><ShieldCheck className="w-6 h-6" /></div>
          <div>
            <div className="font-serif text-ink text-lg">Double authentification</div>
            <div className="text-sm text-foreground/60 mt-0.5">
              {me?.totp_enabled ? "Active — un code TOTP sera demandé à chaque connexion" : "Non activée — recommandée pour les comptes administrateurs"}
            </div>
          </div>
        </div>
        <button onClick={() => setShow2FA(true)} className={me?.totp_enabled ? "btn-outline text-sm" : "btn-primary text-sm"} data-testid="security-2fa-btn">
          {me?.totp_enabled ? "Gérer" : "Activer la 2FA"}
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("logins")}
                className={`px-4 py-2 text-sm rounded-sm border ${tab === "logins" ? "bg-ink text-white border-ink" : "bg-white border-border"}`}
                data-testid="tab-logins">
          Connexions ({loginLogs.length})
        </button>
        <button onClick={() => setTab("actions")}
                className={`px-4 py-2 text-sm rounded-sm border ${tab === "actions" ? "bg-ink text-white border-ink" : "bg-white border-border"}`}
                data-testid="tab-actions">
          Actions admin ({actionLogs.length})
        </button>
        {tab === "logins" && (
          <label className="ml-auto flex items-center gap-2 text-xs">
            <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} />
            Échecs uniquement
          </label>
        )}
      </div>

      {tab === "logins" ? (
        <div className="bg-white border border-border rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                <th className="text-left px-4 py-2.5 font-semibold">Statut</th>
                <th className="text-left px-4 py-2.5 font-semibold">Méthode</th>
                <th className="text-left px-4 py-2.5 font-semibold">IP</th>
              </tr>
            </thead>
            <tbody>
              {loginLogs.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-4 py-2.5 text-xs">{new Date(l.at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2.5">{l.email}</td>
                  <td className="px-4 py-2.5">{l.ok
                      ? <span className="text-emerald-600 font-semibold">Réussi</span>
                      : <span className="text-crimson font-semibold">Échec</span>}</td>
                  <td className="px-4 py-2.5 text-xs">{l.method}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{l.ip || "—"}</td>
                </tr>
              ))}
              {loginLogs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-foreground/55">Aucune entrée</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                <th className="text-left px-4 py-2.5 font-semibold">Admin</th>
                <th className="text-left px-4 py-2.5 font-semibold">Action</th>
                <th className="text-left px-4 py-2.5 font-semibold">Cible</th>
              </tr>
            </thead>
            <tbody>
              {actionLogs.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-4 py-2.5 text-xs">{new Date(l.at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2.5">{l.user_email}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.action}</td>
                  <td className="px-4 py-2.5 text-xs">{l.target_type || "—"} {l.target_id ? `(${l.target_id.slice(0, 8)}...)` : ""}</td>
                </tr>
              ))}
              {actionLogs.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-foreground/55">Aucune entrée</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TwoFactorSettings open={show2FA} onClose={() => { setShow2FA(false); refresh(); }} />
    </div>
  );
}
